import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * SectionHeading — Consistent heading block for page sections.
 *
 * Props:
 * - overline: string — Small colored label above heading (e.g. "Features")
 * - title: string|node — Main heading text
 * - subtitle: string|node — Optional description below heading
 * - align: 'left'|'center' — Text alignment (default: 'left')
 * - maxWidth: number|string — Max width of heading block
 * - accentText: string — Text within title to highlight with accent color
 * - sx: object — Additional sx props for the wrapper
 */
const SectionHeading = ({
  overline,
  title,
  subtitle,
  align = 'left',
  maxWidth,
  accentText,
  sx = {},
}) => {
  const renderTitle = () => {
    if (accentText && typeof title === 'string') {
      const parts = title.split(accentText);
      return (
        <>
          {parts[0]}
          <Typography
            component="span"
            sx={{
              fontWeight: 'inherit',
              fontSize: 'inherit',
              lineHeight: 'inherit',
              letterSpacing: 'inherit',
              color: 'secondary.main',
            }}
          >
            {accentText}
          </Typography>
          {parts[1] || ''}
        </>
      );
    }
    return title;
  };

  return (
    <Box
      sx={{
        textAlign: align,
        maxWidth: maxWidth || 'none',
        mx: align === 'center' ? 'auto' : 0,
        mb: 4,
        ...sx,
      }}
    >
      {overline && (
        <Typography
          variant="overline"
          component="div"
          sx={{ mb: 1 }}
        >
          {overline}
        </Typography>
      )}
      <Typography
        variant="h2"
        component="h2"
        sx={{
          mb: subtitle ? 1.5 : 0,
        }}
      >
        {renderTitle()}
      </Typography>
      {subtitle && (
        <Typography
          variant="subtitle1"
          component="p"
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

export default SectionHeading;
